import React from 'react';
import { Link } from 'react-router-dom';
import DownloadIcon from '../components/DownloadIcon';
import SocialLinks from '../components/SocialLinks';
import MailCopyButton from '../components/MailCopyButton';
import ProjectCard from '../components/ProjectCard';
import LinkRow from '../components/LinkRow';
import HighlightedRowList from '../components/HighlightedRowList';
import ScrollProgressBar from '../components/ScrollProgressBar';
import StackMarquee from '../components/StackMarquee';
import ThumbnailCarousel from '../components/ThumbnailCarousel';
import { caseStudies, getHeroImageSrc } from '../data/caseStudies';
import { bioParagraphs, sideProjects, footerLinks } from '../data/siteData';
import { experience } from '../data/experience';
import { renderInline } from '../utils/renderInline';
import './Home.css';

const toYear = (str) => str.trim().split(' ').pop();

const formatTenure = (roles) => {
  const start = toYear(roles[roles.length - 1].period.split('—')[0]);
  const end = toYear(roles[0].period.split('—')[1]);
  return `${start} — ${end}`;
};

// Same glyph PreviewRow used for external links — kept for visual
// consistency with the rest of the site's "opens in a new tab" cue.
const ExternalLinkIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 17L17 7" />
    <path d="M7 7h10v10" />
  </svg>
);

const SectionHeader = ({ title, to, linked = true }) => {
  const isLink = to && linked;
  const Wrapper = isLink ? Link : 'div';
  const wrapperProps = isLink ? { to } : {};
  return (
    <Wrapper className={`section-header${to ? '' : ' section-header--static'}`} {...wrapperProps}>
      <h2>{title}</h2>
      {to && <span className="arrow">›</span>}
    </Wrapper>
  );
};

const Home = () => {
  return (
    <>
    <ScrollProgressBar />
    <div className="home">
      <div className="page-container home-sections">

        <section className="home-section home-section--about scroll-fade">
          <div className="about-header-row">
            <img
              src="/images/profile.png"
              alt="Joshua Yoon"
              className="about-avatar"
            />
            <div className="about-header-text">
              <h1>Joshua Yoon</h1>
              <p className="about-role">Associate Designer / Design Engineer</p>
            </div>
          </div>
          <div className="about-bio">
            {bioParagraphs.map((para, i) => (
              <p key={i} className="hero-description">{renderInline(para)}</p>
            ))}
            <div className="about-actions">
              <a href="/joshua-yoon-resume.pdf" className="resume-button" download>
                Resume
                <DownloadIcon />
              </a>
              <div className="about-contact-icons">
                <MailCopyButton />
                <SocialLinks links={footerLinks} />
              </div>
            </div>
          </div>
        </section>

        <section className="home-section home-section--carousel scroll-fade">
          <ThumbnailCarousel />
        </section>

        <section className="home-section home-section--experience scroll-fade">
          <SectionHeader title="Experience" />
          <div className="link-rows">
            {experience.map((job) => (
              <LinkRow
                key={job.company}
                title={job.company}
                url={job.url}
                subtitle={job.roles.map((r) => r.role).join(' · ')}
                right={formatTenure(job.roles)}
              />
            ))}
          </div>
        </section>

        <section className="home-section home-section--projects scroll-fade">
          <SectionHeader title="Projects" to="/work" />
          <HighlightedRowList className="project-list-full">
            {(highlight) => caseStudies.map((study) => (
              <ProjectCard
                key={study.id}
                full
                onMouseEnter={highlight.handleRowEnter}
                onMouseLeave={highlight.handleMouseLeave}
                project={{
                  url: `/work/${study.id}`,
                  image: getHeroImageSrc(study),
                  title: study.title,
                  description: study.subtitle,
                  year: study.year,
                }}
              />
            ))}
          </HighlightedRowList>
        </section>

        <Link to="/stack" className="home-section home-section--stack scroll-fade">
          <SectionHeader title="Stack" to="/stack" linked={false} />
          <StackMarquee />
        </Link>

        <section className="home-section home-section--side-projects scroll-fade">
          <SectionHeader title="Side Projects" to="/side-projects" />
          <HighlightedRowList className="link-rows">
            {(highlight) => sideProjects.map((project, index) => (
              <LinkRow
                key={index}
                title={project.title}
                url={project.url}
                subtitle={project.description}
                right={!project.url.startsWith('/') ? <ExternalLinkIcon /> : null}
                stacked
                wholeRowClickable
                onMouseEnter={highlight.handleRowEnter}
                onMouseLeave={highlight.handleMouseLeave}
              />
            ))}
          </HighlightedRowList>
        </section>

      </div>
    </div>
    </>
  );
};

export default Home;
